import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Music2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

export default function Upload() {
  const fileRef = useRef(null);
  const [meta, setMeta] = useState({ languages: [], genres: [], countries: [] });
  const [form, setForm] = useState({ title: "", artist: "", language: "", country: "", genre: "", license_info: "" });
  const [file, setFile] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);

  const load = () => api.get("/me/uploads").then(({ data }) => setMine(data.items));
  useEffect(() => { api.get("/meta").then(({ data }) => setMeta(data)); load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose an audio file."); return; }
    if (!confirmed) { toast.error("Please confirm you own the rights."); return; }
    if (!form.language || !form.country || !form.genre) { toast.error("Please complete all fields."); return; }
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("rights_confirmed", "true");
    fd.append("audio", file);
    try {
      await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Submitted for review!");
      setForm({ title: "", artist: "", language: "", country: "", genre: "", license_info: "" }); setFile(null); setConfirmed(false); load();
    } catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };

  const Sel = ({ k, label, opts }) => (
    <Select value={form[k]} onValueChange={(v) => setForm({ ...form, [k]: v })}>
      <SelectTrigger className="bg-input border-white/10 mt-1.5" data-testid={`up-${k}`}><SelectValue placeholder={`Select ${label}`} /></SelectTrigger>
      <SelectContent className="glass border-white/10 max-h-60">{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-head text-4xl font-black tracking-tighter mb-2">Upload Karaoke / Rap</h1>
      <p className="text-muted-foreground mb-8">Share your original tracks or licensed backing music. All uploads are reviewed before going live.</p>

      <form onSubmit={submit} className="glass rounded-3xl p-8 space-y-5">
        <button type="button" onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-white/15 rounded-2xl p-8 text-center hover:border-neon-pink transition-colors" data-testid="up-file">
          <UploadCloud className="w-8 h-8 mx-auto mb-2 text-neon-pink" />
          <p className="text-sm font-semibold">{file ? file.name : "Choose an audio file"}</p>
          <p className="text-xs text-muted-foreground mt-1">MP3 or WAV · max 40MB</p>
          <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0])} />
        </button>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1.5 bg-input border-white/10" data-testid="up-title" /></div>
          <div><Label className="text-xs">Artist</Label><Input value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })} required className="mt-1.5 bg-input border-white/10" data-testid="up-artist" /></div>
          <div><Label className="text-xs">Language</Label><Sel k="language" label="language" opts={meta.languages} /></div>
          <div><Label className="text-xs">Country</Label><Sel k="country" label="country" opts={meta.countries} /></div>
          <div><Label className="text-xs">Genre</Label><Sel k="genre" label="genre" opts={meta.genres} /></div>
          <div><Label className="text-xs">License info (optional)</Label><Input value={form.license_info} onChange={(e) => setForm({ ...form, license_info: e.target.value })} className="mt-1.5 bg-input border-white/10" /></div>
        </div>

        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <Checkbox checked={confirmed} onCheckedChange={setConfirmed} className="mt-0.5" data-testid="up-confirm" />
          <span className="text-muted-foreground">I confirm that I own or have sufficient rights and permissions to upload and make this content available through SINGVI KARAOKE.</span>
        </label>

        <Button type="submit" disabled={busy} className="w-full rounded-full bg-neon-pink hover:bg-neon-pink/90 font-bold py-6" data-testid="up-submit">{busy ? "Uploading..." : "Submit for review"}</Button>
      </form>

      {mine.length > 0 && (
        <div className="mt-8">
          <h3 className="font-head font-bold mb-3">Your uploads</h3>
          <div className="space-y-2">{mine.map((u) => (
            <div key={u.upload_id} className="card-surface rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3"><Music2 className="w-5 h-5 text-neon-cyan" /><div><p className="font-semibold text-sm">{u.title}</p><p className="text-xs text-muted-foreground">{u.artist} · {u.genre}</p></div></div>
              <span className={`text-xs font-semibold capitalize px-3 py-1 rounded-full ${u.status === "approved" ? "bg-green-500/20 text-green-400" : u.status === "rejected" ? "bg-neon-pink/20 text-neon-pink" : "bg-white/10 text-muted-foreground"}`}>{u.status}</span>
            </div>
          ))}</div>
        </div>
      )}
    </div>
  );
}
